import {StaticStorageService} from './static-storage.service';
import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {concatMap, map, switchMap} from 'rxjs/operators';
import {environment} from './util.service';
import {Update} from '@ngrx/entity';
import {Department, IEmployee, IResponse, IScheduleRotation, ISyncStatus, ITeam, IWeek, WeekDays, IShiftDetail} from '@signal/asp-data-commons';
import {Store} from '@ngrx/store';
import {DealerState} from '../../store/dealer/dealer.reducer';
import {selectDealerCode} from '../../store/dealer/dealer.selectors';
import {v4 as uuid} from 'uuid';

@Injectable({
  providedIn: 'root'
})
export class EmployeeService {

  private apiRoot: string;

  constructor(private readonly http: HttpClient,
              private readonly dealerState: Store<DealerState>,
              private readonly staticStorageService: StaticStorageService) {
    this.apiRoot = environment.aspEmployeeServiceApi;
  }

  getEmployees(): Observable<IResponse<IEmployee[]>> {
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode =>
      this.http.get(`${this.apiRoot}employee/${dealerCode}`).pipe(
      map((data: IResponse<IEmployee[]>) => {
        return data;
      })
      )))
  }

  getEmployee(employeeId: string): Observable<IResponse<IEmployee>> {
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode =>
      this.http.get(`${this.apiRoot}employee/${dealerCode}/${employeeId}`).pipe(
      map((data: IResponse<IEmployee>) => {
        return data;
      })
      )))
  }

  getEmployeesByDepartment(department: string): Observable<IResponse<IEmployee>> {
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode =>
      this.http.get(`${this.apiRoot}employee/${dealerCode}?department=${department}`).pipe(
      map((data: IResponse<IEmployee>) => {
        return data;
      })
      )))
  }

  replaceEmployee(employeeId: string, employee: IEmployee ): Observable<IResponse<IEmployee>> {
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode =>
      this.http.put(`${this.apiRoot}employee/${dealerCode}/${employeeId}`, employee).pipe(
      map((data: IResponse<IEmployee>) => {
        return data;
      })
      )))
  }

  updateEmployee(employeeId: string, employee: IEmployee): Observable<IResponse<IEmployee>> {
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode =>
      this.http.patch(`${this.apiRoot}employee/${dealerCode}/${employeeId}`, employee).pipe(
      map((data: IResponse<IEmployee>) => {
        return data;
      })
      )))
  }

  putEmployees(employees: IEmployee[]): Observable<IResponse<IEmployee>> {
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode =>
      this.http.put(`${this.apiRoot}employee/${dealerCode}`, employees).pipe(
      map((data: IResponse<IEmployee>) => {
        return data;
      })
      )))
  }

  getTeams(): Observable<IResponse<ITeam[]>> {
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode =>
      this.http.get(`${this.apiRoot}team/${dealerCode}`).pipe(
        map((data: IResponse<ITeam[]>) => {
          return data;
        })
      )));
  }

  createTeam(team: Partial<ITeam>): Observable<IResponse<ITeam>>{
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode =>
      this.http.post(`${this.apiRoot}team/${dealerCode}`, team).pipe(
        map((data: IResponse<ITeam>) => {
          return data;
        })
      )));
  }

  updateTeam(team: Update<ITeam>): Observable<IResponse<ITeam>> {
    return this.dealerState.select(selectDealerCode).pipe(concatMap(dealerCode =>
      this.http.put(`${this.apiRoot}team/${dealerCode}/${team.id}`, {id: team.id, ...team.changes}).pipe(
        map((data: IResponse<ITeam>) => {
          return data;
        })
      )))
  }

  putTeam(teams: any[]): Observable<IResponse<ITeam[]>> {
    const requestTeams = teams.map(team => {return {id:team.id , ...team.changes}})
    return this.dealerState.select(selectDealerCode).pipe(
      concatMap(dealerCode =>
      this.http.put(`${this.apiRoot}team/${dealerCode}/updateTeams`,requestTeams ).pipe(
        map((data: IResponse<ITeam[]>) => {
          return data;
        })
      )))
  }

  deleteTeam(id: string): Observable<IResponse<ITeam>> {
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode =>
      this.http.delete(`${this.apiRoot}team/${dealerCode}/${id}`).pipe(
      map((data: IResponse<ITeam>) => {
        return data;
      })
      )))
  }

  uploadEmployeeImage(preSignedUrl: string, file) {
    return this.staticStorageService.saveFile(preSignedUrl, file)
  }

  buildImageUrl(dealerCode: string, employeeId: string,file) {
    const filetype = file.type.split('/')[1];
    return `${environment.spaHost}files/dealers/${dealerCode}/employee-avatar/${employeeId}.${filetype}`;
  }

  getStaffMasterSyncStatus(): Observable<IResponse<ISyncStatus>> {
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode =>
      this.http.get(`${this.apiRoot}employee/${dealerCode}/sync-status`).pipe(
        map((data: IResponse<ISyncStatus>) => {
            return data;
          }
        )
      )));
  }

  triggerStaffMasterSync(): Observable<IResponse<ISyncStatus>> {
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode =>
      //initiate
      this.http.post(`${this.apiRoot}employee/${dealerCode}/initiate-sync`, {}).pipe(
        map((data: IResponse<ISyncStatus>) => {
            return data;
          }
        )
      )));
  }

  getScheduleRotation(department: Department): Observable<IResponse<IScheduleRotation[]>> {
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode =>
      this.http.get(`${this.apiRoot}schedule-rotation/${dealerCode}/${department.toLowerCase()}`).pipe(
        map((data: IResponse<IScheduleRotation[]>) => {
            return data;
          }
        )
      )));
  }

  createScheduleRotation(department,name,payload): Observable<IResponse<IScheduleRotation>> {
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode =>
      this.http.post(`${this.apiRoot}schedule-rotation/${dealerCode}/${department.toLowerCase()}`, payload).pipe(
        map((data: IResponse<IScheduleRotation>) => {
            return data;
          }
        )
      )));
  }

  updateScheduleRotation(department: Department, scheduleRotation: Partial<IScheduleRotation>): Observable<IResponse<IScheduleRotation>> {
    /*return of ({
      status: {code: 200, messages: []},
      timestamp: new Date().toISOString(),
      data: scheduleRotation
    });*/
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode =>
      this.http.put(`${this.apiRoot}schedule-rotation/${dealerCode}/${department.toLowerCase()}/${scheduleRotation.id}`, {...scheduleRotation}).pipe(
        map((data: IResponse<IScheduleRotation>) => {
            return data;
          }
        )
      )));
  }

  deleteScheduleRotation(department: Department, id: string): Observable<IResponse<IScheduleRotation>> {
    /*return of ({
      status: {code: 200, messages: []},
      timestamp: new Date().toISOString(),
      data: scheduleRotation
    });*/
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode =>
      this.http.delete(`${this.apiRoot}schedule-rotation/${dealerCode}/${department.toLowerCase()}/${id}`).pipe(
        map((data: IResponse<IScheduleRotation>) => {
            return data;
          }
        )
      )));
  }
  LoadPreSignedUrl(empId,imageFormat){
    return this.dealerState.select(selectDealerCode).pipe(switchMap(dealerCode =>
      this.http.get(`${this.apiRoot}employee/${dealerCode}/employee-image/${empId}/${imageFormat}`).pipe(
      map((data: IResponse<string>) => {
        return data;
      })
      )))
  }

}

const initialWeekData: IWeek = {
  [WeekDays.MONDAY]: [] as IShiftDetail[],
  [WeekDays.TUESDAY]: [] as IShiftDetail[],
  [WeekDays.WEDNESDAY]: [] as IShiftDetail[],
  [WeekDays.THURSDAY]: [] as IShiftDetail[],
  [WeekDays.FRIDAY]: [] as IShiftDetail[],
  [WeekDays.SATURDAY]: [] as IShiftDetail[],
  [WeekDays.SUNDAY]: [] as IShiftDetail[],
};

